import React, { useEffect, useState } from 'react';
import { createClientStripeOnboardingLink, getClientDashboard } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';

const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(cents || 0) / 100);

export default function ClientDashboardPage() {
  const { theme: T } = useClientTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    let active = true;
    getClientDashboard()
      .then((data) => { if (active) setDashboard(data?.dashboard || null); })
      .catch((err) => { if (active) setError(err?.response?.data?.error || 'Failed to load dashboard.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return <Spinner T={T} />;
  if (error) return <ErrorBanner T={T}>{error}</ErrorBanner>;

  const card = { borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18 };
  const attention = Array.isArray(dashboard?.attention) ? dashboard.attention : [];
  const collectionPreview = Array.isArray(dashboard?.collectionPreview) ? dashboard.collectionPreview : [];
  const activeCampaign = Array.isArray(dashboard?.campaigns) ? dashboard.campaigns[0] : null;
  const agreementAccepted = dashboard?.agreement?.status === 'accepted';
  const launch = dashboard?.launch || null;

  const startStripeConnect = async () => {
    setStripeLoading(true);
    setError('');
    try {
      const data = await createClientStripeOnboardingLink();
      if (!data?.url) throw new Error('Stripe did not return an onboarding link.');
      window.location.assign(data.url);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open Stripe Connect.');
      setStripeLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 750, color: T.text }}>Overview</div>
        <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>
          {dashboard?.brand?.name || 'Brand'} status, launch readiness, and current drop signal.
        </div>
      </div>

      <section style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>Status Summary</div>
          {launch ? (
            <span style={{
              borderRadius: 999,
              border: `1px solid ${launch.ready ? 'rgba(34,197,94,0.42)' : T.border}`,
              color: launch.ready ? '#22c55e' : T.textSub,
              padding: '4px 9px',
              fontSize: 10,
              fontWeight: 850,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}>
              {launch.statusLabel}
            </span>
          ) : null}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
          <MiniMetric label="Drop" value={activeCampaign ? (activeCampaign.status || 'Active') : 'None'} T={T} />
          <MiniMetric label="Agreement" value={agreementAccepted ? 'Accepted' : 'Needs review'} T={T} />
          <MiniMetric label="Payments" value={launch?.stripeConnect?.statusLabel || 'Unknown'} T={T} />
          <MiniMetric label="Storefront" value={launch?.storefrontPublished ? 'Published' : 'Not published'} T={T} />
        </div>
        {launch?.storefrontUrl ? (
          <a href={launch.storefrontUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', marginTop: 12, color: T.gold, fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>
            Open storefront
          </a>
        ) : null}
      </section>

      <section style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>Needs Your Attention</div>
        {attention.length ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {attention.map((item, index) => (
              item.type === 'stripe_connect' ? (
                <button
                  type="button"
                  onClick={startStripeConnect}
                  disabled={stripeLoading}
                  key={`${item.type}-${index}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    borderRadius: 8,
                    border: `1px solid ${T.border}`,
                    padding: '11px 12px',
                    color: T.text,
                    fontSize: 13,
                    background: T.id === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.02)',
                    cursor: stripeLoading ? 'wait' : 'pointer',
                    opacity: stripeLoading ? 0.68 : 1,
                  }}
                >
                  {stripeLoading ? 'Opening Stripe...' : 'Connect Stripe payment setup'}
                </button>
              ) : (
                <a href={item.path || '#'} key={`${item.type}-${index}`} style={{ display: 'block', borderRadius: 8, border: `1px solid ${T.border}`, padding: '11px 12px', color: T.text, fontSize: 13, background: T.id === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.02)', textDecoration: 'none' }}>
                  {item.label}
                </a>
              )
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: T.textMuted }}>Nothing is waiting on you right now.</div>
        )}
      </section>

      <section style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>Collection Preview</div>
          <div style={{ fontSize: 11, color: T.textMuted }}>{collectionPreview.length} shared</div>
        </div>
        {collectionPreview.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            {collectionPreview.slice(0, 4).map((item) => (
              <div key={item._id || item.id} style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                <div style={{ aspectRatio: '1 / 1', background: T.id === 'light' ? '#eef1f5' : '#111' }}>
                  {item.imageUrl ? <img src={item.imageUrl} alt={item.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                </div>
                <div style={{ padding: 9, fontSize: 12, color: T.text, fontWeight: 700 }}>{item.title || 'Untitled'}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: T.textMuted }}>No collection items have been shared yet.</div>
        )}
      </section>

      <details style={card}>
        <summary style={{ cursor: 'pointer', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>The Numbers</summary>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 14 }}>
          <MiniMetric label="Revenue" value={formatMoney(dashboard?.sales?.revenueCents || 0)} T={T} />
          <MiniMetric label="Reservations" value={dashboard?.sales?.reservations || 0} T={T} />
          <MiniMetric label="Units" value={dashboard?.sales?.units || 0} T={T} />
          <MiniMetric label="Visitors" value={dashboard?.analytics?.visitors || 0} T={T} />
          <MiniMetric label="Page Views" value={dashboard?.analytics?.pageViews || 0} T={T} />
          <MiniMetric label="Cart Adds" value={dashboard?.analytics?.cartAdds || 0} T={T} />
          <MiniMetric label="Checkout Starts" value={dashboard?.analytics?.checkoutStarts || 0} T={T} />
        </div>
        <a href="/client-portal/performance" style={{ display: 'inline-flex', marginTop: 12, color: T.gold, fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>
          View performance detail
        </a>
      </details>
    </div>
  );
}

function MiniMetric({ label, value, T }) {
  return (
    <div style={{ borderRadius: 8, border: `1px solid ${T.border}`, padding: 12 }}>
      <div style={{ fontSize: 10, color: T.label, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 20, color: T.text, fontWeight: 800, marginTop: 5 }}>{value}</div>
    </div>
  );
}

export function Spinner({ T }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
      <div style={{ width: 22, height: 22, border: `2px solid ${T.border}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function ErrorBanner({ T, children }) {
  return <div style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${T.errorBorder}`, background: T.errorBg, color: T.errorText, fontSize: 13 }}>{children}</div>;
}
